import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import {
  extractClarificationQuestions,
  generateClarifications,
  saveClarificationsToGlobalQA,
} from '../../services/procurementService';
import storageService from '../../services/storageService';

interface Clarification {
  id: string;
  question: string;
  answer: string;
  round?: number;
  isLocked?: boolean;
}

interface ClarificationsViewProps {
  procurementId: string;
  initialClarifications?: Clarification[];
  onUpdate?: (clarifications: Clarification[]) => void;
  onShowSnackbar: (msg: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const ClarificationsView: React.FC<ClarificationsViewProps> = ({
  procurementId,
  initialClarifications = [],
  onUpdate,
  onShowSnackbar,
}) => {
  const { account } = useAccount();
  const [clarifications, setClarifications] = useState<Clarification[]>(initialClarifications);
  const [newQuestion, setNewQuestion] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentRound, setCurrentRound] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [savedClarificationIds, setSavedClarificationIds] = useState<Set<string>>(new Set());
  const [forceGuess, setForceGuess] = useState(false);

  // In a real implementation, this might be fetched from a file or existing search context.
  // For this scope, we just mock the context or allow the backend to fetch it.
  const evidenceContext = 'Procurement guidelines and previous Q&A references.';

  const handleAddQuestion = () => {
    if (!newQuestion.trim()) {
      return;
    }
    const updated = [
      ...clarifications,
      {
        id: crypto.randomUUID(),
        question: newQuestion.trim(),
        answer: '',
        round: currentRound,
        isLocked: false,
      },
    ];
    setClarifications(updated);
    if (onUpdate) {
      onUpdate(updated);
    }
    setNewQuestion('');
  };

  const handleGenerateResponses = async () => {
    if (!account?.clientId) {
      return;
    }
    const questionsToProcess =
      selectedIds.size > 0
        ? clarifications.filter((c) => selectedIds.has(c.id) && !c.isLocked)
        : clarifications.filter((c) => !c.isLocked);

    if (questionsToProcess.length === 0) {
      onShowSnackbar('No pending or unlocked questions to answer in selection.', 'info');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await generateClarifications(
        procurementId,
        account.clientId,
        questionsToProcess.map((q) => ({ id: q.id, text: q.question })),
        evidenceContext,
        forceGuess
      );

      if (response.success && response.clarifications) {
        const updatedClarifications = clarifications.map((c) => {
          const updated = response.clarifications?.find((res) => res.id === c.id);
          return updated ? { ...c, answer: updated.answer } : c;
        });
        setClarifications(updatedClarifications);
        if (onUpdate) {
          onUpdate(updatedClarifications);
        }
        onShowSnackbar('AI responses generated successfully.', 'success');
      } else {
        throw new Error(response.error || 'Failed to generate responses.');
      }
    } catch (error: unknown) {
      onShowSnackbar((error as Error).message, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !account?.clientId || !account?.uid || !account?.teamId) {
      return;
    }

    setIsUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `clients/${account.clientId}/teams/${account.teamId}/users/${account.uid}/procurements/${procurementId}/clarifications_in/${crypto.randomUUID()}_${safeName}`;

      onShowSnackbar('Uploading document...', 'info');
      await storageService.uploadFile(storagePath, file);

      onShowSnackbar('Extracting questions...', 'info');
      const response = await extractClarificationQuestions(
        procurementId,
        account.clientId,
        storagePath
      );

      if (response.success && response.questions && response.questions.length > 0) {
        const newClarifications = response.questions.map((q) => ({
          id: crypto.randomUUID(),
          question: q,
          answer: '',
          round: currentRound,
        }));
        const updated = [...clarifications, ...newClarifications];
        setClarifications(updated);
        if (onUpdate) {
          onUpdate(updated);
        }
        onShowSnackbar(`Extracted ${response.questions.length} questions successfully.`, 'success');
      } else {
        throw new Error(response.error || 'No questions found or extraction failed.');
      }
    } catch (error: unknown) {
      onShowSnackbar((error as Error).message, 'error');
    } finally {
      setIsUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleSaveToGlobalQA = async () => {
    if (!account?.clientId) {
      return;
    }
    const clarificationsToSave =
      selectedIds.size > 0
        ? clarifications.filter((c) => selectedIds.has(c.id) && !savedClarificationIds.has(c.id))
        : clarifications.filter((c) => !savedClarificationIds.has(c.id));

    if (clarificationsToSave.length === 0) {
      onShowSnackbar('No unsaved clarifications to save in the current selection.', 'info');
      return;
    }

    setIsSaving(true);
    try {
      const response = await saveClarificationsToGlobalQA(
        procurementId,
        account.clientId,
        clarificationsToSave
      );
      if (response.success) {
        setSavedClarificationIds(
          new Set([...savedClarificationIds, ...clarificationsToSave.map((c) => c.id)])
        );
        onShowSnackbar('Saved to Global Q&A repository successfully.', 'success');
      } else {
        throw new Error(response.error || 'Failed to save to Global Q&A.');
      }
    } catch (error: unknown) {
      onShowSnackbar((error as Error).message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportCSV = () => {
    const questionsToExport =
      selectedIds.size > 0 ? clarifications.filter((c) => selectedIds.has(c.id)) : clarifications;

    if (questionsToExport.length === 0) {
      onShowSnackbar('No clarifications to export.', 'info');
      return;
    }

    const csvContent = [
      ['Tranche', 'Question', 'Answer'].join(','),
      ...questionsToExport.map((q) =>
        [
          q.round || 1,
          `"${q.question.replace(/"/g, '""')}"`,
          `"${(q.answer || '').replace(/"/g, '""')}"`,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `clarifications_${procurementId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    onShowSnackbar('Clarifications exported successfully.', 'success');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Supplier Clarifications - Q&A
        </Typography>
        <Box component="ul" sx={{ mt: 0, pl: 2, color: 'text.secondary', '& li': { mb: 0.5 } }}>
          <Typography component="li" variant="body2">
            Manage all Q&A and clarifications with suppliers.
          </Typography>
          <Typography component="li" variant="body2">
            Add questions manually or upload supplier documents (e.g., PDF, Excel) to auto-extract
            them using AI.
          </Typography>
          <Typography component="li" variant="body2">
            Once answered, generate an Addendum or save to the global Knowledge Base.
          </Typography>
          <Typography component="li" variant="body2">
            Use the lock icon to confirm and lock an answer. Locked rows will not be sent or
            overwritten when the Generate button is pressed.
          </Typography>
        </Box>
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={forceGuess}
                onChange={(e) => setForceGuess(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 500, color: 'text.primary', mr: 1 }}>
                  Force Responses:
                </Box>
                Forces the AI to provide a reasonable response based on industry standards even if
                the explicit answer isn't found in your documents.
              </Typography>
            }
            sx={{ ml: 0 }}
          />
        </Box>
      </Box>

      <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <TextField
          select
          size="small"
          label="Tranche"
          value={currentRound}
          onChange={(e) => setCurrentRound(Number(e.target.value))}
          sx={{ minWidth: 140 }}
        >
          {[1, 2, 3, 4, 5].map((round) => (
            <MenuItem key={round} value={round}>
              T{round}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          placeholder="Enter a supplier question..."
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
        />
        <Button
          variant="contained"
          onClick={handleAddQuestion}
          disabled={!newQuestion.trim()}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap', mt: 0.5 }}
        >
          Add Question
        </Button>
        <Button
          variant="contained"
          component="label"
          sx={{
            bgcolor: 'bidlogiq.brick',
            color: '#fff',
            '&:hover': { bgcolor: 'bidlogiq.rust' },
            flexShrink: 0,
            whiteSpace: 'nowrap',
            mt: 0.5,
          }}
          startIcon={
            isUploading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />
          }
          disabled={isUploading}
        >
          Upload Questions
          <input
            type="file"
            hidden
            accept=".pdf,.docx,.txt,.xlsx,.xls,.csv"
            onChange={handleFileUpload}
          />
        </Button>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            sx={{
              bgcolor: 'bidlogiq.brick',
              color: '#fff',
              '&:hover': { bgcolor: 'bidlogiq.rust' },
            }}
            startIcon={
              isGenerating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />
            }
            onClick={handleGenerateResponses}
            disabled={isGenerating || clarifications.filter((c) => !c.isLocked).length === 0}
          >
            Generate Responses
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={isSaving ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            onClick={handleSaveToGlobalQA}
            disabled={isSaving || clarifications.length === 0}
          >
            Save to Global Q&A
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={clarifications.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {clarifications.length === 0 ? (
        <Alert severity="info">No clarification questions added yet.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead
              sx={{
                bgcolor: (theme) => theme.palette.greyContrast,
                '& .MuiTableCell-root': {
                  color: (theme) => theme.palette.getContrastText(theme.palette.greyContrast),
                  fontWeight: 'bold',
                },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    sx={{
                      color: 'inherit',
                      '&.Mui-checked': { color: 'inherit' },
                      '&.MuiCheckbox-indeterminate': { color: 'inherit' },
                    }}
                    indeterminate={selectedIds.size > 0 && selectedIds.size < clarifications.length}
                    checked={
                      clarifications.length > 0 && selectedIds.size === clarifications.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(new Set(clarifications.map((c) => c.id)));
                      } else {
                        setSelectedIds(new Set());
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: '120px' }}>Tranche</TableCell>
                <TableCell sx={{ width: '40%' }}>Supplier Question</TableCell>
                <TableCell sx={{ width: '60%' }}>Response (click to edit)</TableCell>
                <TableCell sx={{ width: '50px' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {clarifications.map((row) => {
                const isSelected = selectedIds.has(row.id);
                const isNotFound = row.answer === 'Response not found in provided documents';
                const hasAnswer = Boolean(row.answer);

                let rowBgColor: string | ((theme: Theme) => string) = isSelected
                  ? 'action.selected'
                  : 'inherit';
                if (row.isLocked) {
                  rowBgColor = (theme) => alpha(theme.palette.success.light, 0.2);
                } else if (isNotFound) {
                  rowBgColor = (theme) => alpha(theme.palette.error.light, 0.2);
                } else if (hasAnswer) {
                  rowBgColor = (theme) => alpha(theme.palette.warning.light, 0.2);
                }

                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      bgcolor: rowBgColor,
                      // Ensure text doesn't change color when selected
                      '& .MuiTableCell-root': {
                        color: 'text.primary',
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="default"
                        checked={isSelected}
                        onChange={(e) => {
                          const newSelected = new Set(selectedIds);
                          if (e.target.checked) {
                            newSelected.add(row.id);
                          } else {
                            newSelected.delete(row.id);
                          }
                          setSelectedIds(newSelected);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{ minWidth: 60 }}
                        value={row.round || 1}
                        onChange={(e) => {
                          const newClarifications = clarifications.map((c) =>
                            c.id === row.id ? { ...c, round: Number(e.target.value) } : c
                          );
                          setClarifications(newClarifications);
                          if (onUpdate) {
                            onUpdate(newClarifications);
                          }
                        }}
                      >
                        {[1, 2, 3, 4, 5].map((r) => (
                          <MenuItem key={r} value={r}>
                            T{r}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>{row.question}</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        multiline
                        size="small"
                        variant="standard"
                        placeholder="..."
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          '& .MuiInputBase-input::placeholder': {
                            fontStyle: 'italic',
                            opacity: 0.7,
                          },
                        }}
                        value={row.answer || ''}
                        onChange={(e) => {
                          const newClarifications = clarifications.map((c) =>
                            c.id === row.id ? { ...c, answer: e.target.value } : c
                          );
                          setClarifications(newClarifications);
                          if (onUpdate) {
                            onUpdate(newClarifications);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newClarifications = clarifications.map((c) =>
                            c.id === row.id ? { ...c, isLocked: !c.isLocked } : c
                          );
                          setClarifications(newClarifications);
                          if (onUpdate) {
                            onUpdate(newClarifications);
                          }
                        }}
                      >
                        {row.isLocked ? (
                          <LockIcon fontSize="small" color="success" />
                        ) : (
                          <LockOpenIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ClarificationsView;
